"use client";

import React from "react";
import Link from "next/link";
import { FileText, Calendar, ArrowLeft, Info } from "@phosphor-icons/react";

export default function TermsOfService() {
  return (
    <section className="w-full min-h-screen bg-slate-950 text-slate-100 py-16 px-4 md:py-24">
      <div className="max-w-4xl mx-auto space-y-12">
        {/* Navigation back */}
        <Link 
          href="/" 
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-wider"
        >
          <ArrowLeft size={16} /> Back to Home
        </Link>

        {/* Title Section */}
        <div className="border-b border-slate-800 pb-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-400">
              <FileText size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Terms and Conditions</h1>
              <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mt-1">SOFAR Terms & Conditions of Enrollment</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 pt-2">
            <span className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 px-3 py-1 rounded-full">
              <Calendar size={14} className="text-blue-400" />
              Last Updated: June 6, 2026
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="space-y-8 text-sm text-slate-300 leading-relaxed">
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">1. Enrollment and Account Security</h2>
            <p>
              By enrolling in any program on `thesofar.org`, you agree to provide complete, accurate, and current information. Each registered account is personal, and sharing logins is strictly prohibited. To safeguard our digital rights, we monitor and limit concurrent login locations and session signatures.
            </p>
          </div>

          {/* Special Refund & Cancellation Block */}
          <div className="p-6 rounded-2xl bg-blue-600/5 border border-blue-500/20 space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Info size={20} className="text-blue-400" />
              2. Refund and Cancellation Policy
            </h2>
            <p className="text-slate-300">
              We strive to deliver top-tier preparation modules for CFA, FRM, and financial certification streams. If you seek a refund, the cancellation is governed by the following three strict rules:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="block text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Rule 1: Window</span>
                <strong className="block text-white text-base">7-Day Eligibility</strong>
                <span className="block text-[11px] text-slate-400">Refund requests must be submitted within 7 calendar days of the course purchase date.</span>
              </div>
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="block text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Rule 2: Usage Limit</span>
                <strong className="block text-white text-base">Less than 25% Progress</strong>
                <span className="block text-[11px] text-slate-400">You must have accessed or watched less than 25% of the total course syllabus content.</span>
              </div>
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="block text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Rule 3: Settlement</span>
                <strong className="block text-white text-base">7–15 Business Days</strong>
                <span className="block text-[11px] text-slate-400">Approved refunds will be processed and credited back to your original source within 7–15 working days.</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 pt-1">
              To initiate a cancellation, please email your receipt and request to our support cell: **sofarindia.helpdesk@gmail.com**. Course progress figures are calculated automatically by our backend database logs and are final.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">3. Intellectual Property Rights</h2>
            <p>
              All video streams, question banks, study guides, comment lists, and web assets are the exclusive intellectual property of SOFAR. Copying, recording, screen-capturing, downloading, or distributing course materials without authorization will lead to immediate account termination and legal action under local intellectual property laws.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">4. User Conduct and Chat Forums</h2>
            <p>
              We provide comment boards on course player pages to support doubt-solving. Users must remain polite and constructive. Any offensive speech, spamming, advertisement of third-party portals, or harassment of instructors will result in comment blockages or account bans.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">5. DPDPA Consent and Right of Withdrawal</h2>
            <p>
              Your student dashboard and tracking features are active subject to the consents you granted. You can review how your data is protected under India&apos;s DPDP Act 2023 in our <Link href="/privacy-policy" className="text-blue-400 hover:underline">Privacy Policy</Link>.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">6. Limit of Liability</h2>
            <p>
              SOFAR represents training support to help students prepare for third-party certifications (e.g. CFA Institute, GARP). We do not guarantee passing scores, and we are not liable for examination registration fees, travel expenses, or other incidentals.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">7. Governing Law and Dispute Redressal</h2>
            <p>
              These Terms and Conditions are governed by and construed in accordance with the laws of India. Any disputes arising out of your enrollment will be subject to the exclusive jurisdiction of the competent courts in New Delhi, India.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
